import { useState } from "react";
import { Button, Input, Modal } from "antd";
import { useMutation, useQueryClient } from "react-query";
import { verifyUserBvn } from "../../api/user";
import { showErrorToast, showSuccessToast } from "../../utils/toast";
import { usePermissionEffect } from "../../utils/usePermissionEffect";

const EMPTY = { bvn: "" };

// Opens a form to retry BVN verification for a customer who isn't verified.
// Renders nothing without the Customer: canUpdate permission.
export default function VerifyBvnButton({ userId, name, hasBvn, size = "middle" }) {
  const queryClient = useQueryClient();
  const { getModulePermission } = usePermissionEffect();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const mutation = useMutation(verifyUserBvn, {
    onSuccess: (r) => {
      showSuccessToast(r?.message || "BVN verified");
      setOpen(false);
      setForm(EMPTY);
      queryClient.invalidateQueries("customers");
      queryClient.invalidateQueries("user-detail");
    },
    // a watchlist hit deactivates the account, so refresh either way
    onError: (e) => {
      showErrorToast(e.message);
      queryClient.invalidateQueries("customers");
      queryClient.invalidateQueries("user-detail");
    },
  });

  if (!getModulePermission("customer", "canUpdate")) return null;

  const bvnValid = /^\d{11}$/.test(form.bvn);
  const canSubmit = (hasBvn || bvnValid) && (!form.bvn || bvnValid) && !mutation.isLoading;

  return (
    <>
      <Button size={size} onClick={() => setOpen(true)}>
        Verify BVN
      </Button>
      <Modal
        title={`Verify BVN${name ? ` for ${name}` : ""}`}
        open={open}
        onCancel={() => !mutation.isLoading && setOpen(false)}
        okText={mutation.isLoading ? "Verifying…" : "Verify"}
        okButtonProps={{ disabled: !canSubmit }}
        onOk={() => mutation.mutate({ id: userId, ...form })}
      >
        <div className="space-y-3 py-2">
          <div>
            <div className="text-[13px] mb-1">BVN{hasBvn ? " (optional)" : ""}</div>
            <Input
              placeholder={hasBvn ? "Leave blank to use the BVN on file" : "11 digits"}
              value={form.bvn}
              maxLength={11}
              onChange={(e) => setForm((f) => ({ ...f, bvn: e.target.value.replace(/\D/g, "") }))}
              status={form.bvn && !bvnValid ? "error" : undefined}
            />
          </div>
          <div className="text-[12px] text-[#00000080]">
            The customer's first and last name must match the BVN record.
            Each attempt is a paid lookup. A watchlisted BVN deactivates the account.
          </div>
        </div>
      </Modal>
    </>
  );
}
